import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from "@mui/material";

const buildChipTones = (palette) => ({
  success: { bg: palette.secondary.main, color: palette.common.white },
  info: { bg: palette.primary.main, color: palette.common.white },
  pending: { bg: palette.warning.main, color: palette.common.white },
  neutral: { bg: palette.backgroundGray.main, color: palette.text.secondary }
});

export const StatusChip = ({ label, tone }) => {
  const { palette } = useTheme();
  const toneStyle = buildChipTones(palette)[tone] || buildChipTones(palette).neutral;
  return (
    <Box
      display="inline-flex"
      px={1}
      py={0.25}
      sx={{ borderRadius: "12px", backgroundColor: toneStyle.bg }}
    >
      <Typography variant="caption" style={{ color: toneStyle.color }}>
        {label}
      </Typography>
    </Box>
  );
};

const STAT_TONE_COLOR = {
  total: "textPrimary",
  success: "secondary",
  neutral: "textPrimary",
  warning: "error"
};

const StatCard = ({ label, value, tone }) => (
  <Paper
    elevation={1}
    sx={{ p: 2, borderRadius: "8px", flex: 1, minWidth: 150 }}
  >
    <Typography variant="overline" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="h4" color={STAT_TONE_COLOR[tone] || "textPrimary"}>
      {value}
    </Typography>
  </Paper>
);

const SEGMENT_TONE_COLOR = (palette) => ({
  success: palette.secondary.main,
  info: palette.primary.main,
  pending: palette.warning.main,
  neutral: palette.backgroundGray.dark
});

const StatusBar = ({ title, segments }) => {
  const { palette } = useTheme();
  const segmentColor = SEGMENT_TONE_COLOR(palette);
  const total = segments.reduce((sum, segment) => sum + segment.value, 0) || 1;

  return (
    <Paper elevation={1} sx={{ p: 2, borderRadius: "8px" }}>
      <Box mb={1.5}>
        <Typography variant="subtitle1" color="textPrimary">
          {title}
        </Typography>
      </Box>
      <Box
        display="flex"
        sx={{ height: 12, borderRadius: "6px", overflow: "hidden", gap: "2px" }}
      >
        {segments.map((segment) => (
          <Box
            key={segment.label}
            sx={{
              width: `${(segment.value / total) * 100}%`,
              backgroundColor:
                segmentColor[segment.tone] || palette.backgroundGray.dark
            }}
          />
        ))}
      </Box>
      <Box mt={1.5} display="flex" flexWrap="wrap" gap={2}>
        {segments.map((segment) => (
          <Box
            key={segment.label}
            display="flex"
            alignItems="center"
            gap={1}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor:
                  segmentColor[segment.tone] || palette.backgroundGray.dark
              }}
            />
            <Typography variant="caption" color="textSecondary">
              {`${segment.label} · ${segment.value}`}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const CollectionTable = ({ title, columns, rows, emptyText }) => {
  const { palette } = useTheme();
  const isEmpty = !rows || rows.length === 0;
  return (
    <Paper elevation={1} sx={{ borderRadius: "8px", overflow: "hidden" }}>
      <Box
        px={2}
        py={1.5}
        sx={{ borderBottom: `1px solid ${palette.backgroundGray.main}` }}
      >
        <Typography variant="subtitle1" color="textPrimary">
          {title}
        </Typography>
      </Box>
      {isEmpty && (
        <Box p={3}>
          <Typography variant="body2" color="textSecondary">
            {emptyText || "Nothing here yet."}
          </Typography>
        </Box>
      )}
      {!isEmpty && (
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} align={column.align || "left"}>
                  <Typography variant="caption" color="textSecondary">
                    {column.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={row.rowKey || row.id || rowIndex}>
                {columns.map((column) => (
                  <TableCell key={column.key} align={column.align || "left"}>
                    {column.render ? (
                      column.render(row)
                    ) : (
                      <Typography variant="body2" color="textPrimary">
                        {row[column.key]}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
};

const CollectionScreen = ({
  heading,
  subtitle,
  headerAction,
  stats,
  statusTitle,
  statusSegments,
  tableTitle,
  columns,
  rows,
  emptyText
}) => (
  <Box display="flex" flexDirection="column" gap={3} sx={{ minWidth: 0 }}>
    <Box
      display="flex"
      alignItems="flex-start"
      justifyContent="space-between"
      gap={2}
    >
      <Box>
        <Typography variant="h4" color="textPrimary">
          {heading}
        </Typography>
        {subtitle && (
          <Typography variant="subtitle1" color="textSecondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {headerAction}
    </Box>

    <Box display="flex" gap={2} flexWrap="wrap" alignItems="stretch">
      {stats.map((stat) => (
        <StatCard
          key={stat.label}
          label={stat.label}
          value={stat.value}
          tone={stat.tone}
        />
      ))}
    </Box>

    {statusSegments && statusSegments.some((s) => s.value > 0) && (
      <StatusBar title={statusTitle} segments={statusSegments} />
    )}

    <CollectionTable
      title={tableTitle}
      columns={columns}
      rows={rows}
      emptyText={emptyText}
    />
  </Box>
);

export default CollectionScreen;
